import { displayRate } from '../util';
import { EstateStatusItemState } from '../generated/graphql';
import { ESTATE_STATUS_PERCENT_SCALE, ESTATE_STATUS_SCORE_DECIMAL_MULTIPLIER } from './constants';
import {
  EstateStatusAttachmentLink,
  EstateStatusChecklistItem,
  EstateStatusReadOnlyChecklistItem,
  SaveEstateStatusChecklistItemInput,
  EstateStatusSummary,
} from './types';

export function isEstateStatusAttachmentSavable(attachmentLink: Pick<EstateStatusAttachmentLink, 'fileName' | 'url'>) {
  return attachmentLink.fileName.trim().length > 0 && attachmentLink.url.trim().length > 0;
}

export function buildEstateStatusAttachmentSaveInput(attachmentLinks: EstateStatusAttachmentLink[]) {
  return attachmentLinks.filter(isEstateStatusAttachmentSavable).map((attachmentLink, attachmentIndex) => ({
    id: attachmentLink.id ?? `estate-status-attachment-${attachmentIndex + 1}`,
    fileName: attachmentLink.fileName.trim(),
    url: attachmentLink.url.trim(),
  }));
}

export function normalizeEstateStatusDateTime(value: string | null | undefined) {
  if (!value) return null;

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? value : new Date(timestamp).toISOString();
}

export function buildEstateStatusChecklistSaveInput(
  checklistToSave: EstateStatusChecklistItem[]
): SaveEstateStatusChecklistItemInput[] {
  return checklistToSave.map((item) => ({
    id: item.id,
    state: item.state,
    lastReviewedAt: normalizeEstateStatusDateTime(item.lastReviewedAt),
    note: item.note ?? '',
    attachmentLinks: buildEstateStatusAttachmentSaveInput(item.attachmentLinks),
  }));
}

function cloneEstateStatusAttachmentLinks(attachmentLinks: EstateStatusAttachmentLink[] | undefined | null) {
  return (attachmentLinks ?? []).map((attachmentLink) => ({ ...attachmentLink }));
}

function cloneEstateStatusChecklistItem(item: EstateStatusChecklistItem): EstateStatusChecklistItem {
  return {
    ...item,
    note: item.note ?? '',
    lastReviewedAt: item.lastReviewedAt ?? null,
    attachmentLinks: cloneEstateStatusAttachmentLinks(item.attachmentLinks),
  };
}

export function cloneEstateStatusChecklist(checklist: EstateStatusChecklistItem[]) {
  return checklist.map(cloneEstateStatusChecklistItem);
}

export function calculateEstateStatusSummary(checklist: EstateStatusReadOnlyChecklistItem[]): EstateStatusSummary {
  const completed = checklist.reduce((sum, item) => sum + (item.state === EstateStatusItemState.Yes ? 1 : 0), 0);
  const applicable = checklist.reduce((sum, item) => sum + (item.state === EstateStatusItemState.Na ? 0 : 1), 0);
  const total = checklist.length;
  const totalWeight = checklist.reduce((sum, item) => sum + item.weight, 0);
  const applicableWeight = checklist.reduce(
    (sum, item) => sum + (item.state === EstateStatusItemState.Na ? 0 : item.weight),
    0
  );
  const earnedWeight = checklist.reduce(
    (sum, item) => sum + (item.state === EstateStatusItemState.Yes ? item.weight : 0),
    0
  );
  let score: number | null = null;

  if (applicableWeight > 0) {
    const rawScorePercent = (earnedWeight / applicableWeight) * ESTATE_STATUS_PERCENT_SCALE;
    score =
      Math.round(rawScorePercent * ESTATE_STATUS_SCORE_DECIMAL_MULTIPLIER) / ESTATE_STATUS_SCORE_DECIMAL_MULTIPLIER;
  }

  return {
    completed,
    applicable,
    total,
    earnedWeight,
    applicableWeight,
    totalWeight,
    score,
  };
}

export function formatEstateStatusWeight(value: number) {
  return displayRate(value / ESTATE_STATUS_PERCENT_SCALE, { zeroIsDash: false });
}
